import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, History, MapPin, Pencil, RotateCcw } from "lucide-react";
import { fetchPlan, savePlan, fetchCourseMatches } from "@/pages/beta/courseFitApi";
import { useJourney } from "@/context/JourneyContext";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
};
const btn = "inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] focus-visible:ring-offset-2";
const kicker = "text-[11px] uppercase tracking-[0.14em] text-[#7C3AED]";

// ---------------- saved result bar ----------------
export function SavedResultBar({ saved, completedAt, onStartOver }) {
  if (!saved?.signedIn) {
    return (
      <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900 print:hidden" data-testid="fm-saved-anon">
        This result is kept in this browser tab only. Sign in to keep it with your account and reopen it later.
      </div>
    );
  }
  const earlier = (saved.history || []).filter((a) => a.sessionId !== saved.sessionId);
  return (
    <section className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 print:hidden" data-testid="fm-saved-bar">
      <div className={kicker}>{saved.sessionId ? "Saved to your account" : "Saving…"}</div>
      <p className="mt-1 text-sm text-stone-700" data-testid="fm-saved-date">Completed {fmtDate(completedAt)}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {saved.sessionId && (
          <button type="button" onClick={() => saved.onEdit(saved.sessionId)} className={`${btn} border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F0FF]`} data-testid="fm-edit-answers">
            <Pencil className="h-4 w-4" aria-hidden /> Edit my answers
          </button>
        )}
        <button type="button" onClick={onStartOver} className={`${btn} border-stone-300 text-stone-800 hover:bg-stone-50`} data-testid="fm-start-again">
          <RotateCcw className="h-4 w-4" aria-hidden /> Start again
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">Either way this result stays saved, and your shortlist isn't changed.</p>
      {saved.error && <p role="alert" className="mt-2 text-sm text-rose-700" data-testid="fm-saved-error">{saved.error}</p>}
      {earlier.length > 0 && (
        <div className="mt-5 border-t border-stone-100 pt-4" data-testid="fm-history">
          <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600"><History className="h-3.5 w-3.5" aria-hidden /> Earlier results</div>
          <ul className="mt-2 space-y-1">
            {earlier.map((a) => (
              <li key={a.sessionId}>
                <button type="button" onClick={() => saved.onOpen(a.sessionId)} data-testid={`fm-history-open-${a.sessionId}`}
                  className="w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]">
                  <span className="font-medium text-stone-900">{fmtDate(a.completedAt)}</span>
                  <span className="text-stone-500"> · {a.topSubjects.join(", ")}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---------------- interests → separate course pools ----------------
function rankNote(subject, top5) {
  const row = top5.find((r) => r.canonicalSubject === subject);
  return row ? `Fast Match #${row.rank} · ${row.label}` : "Also worth exploring · not scored";
}

export function CoursePool({ subject, role, top5, anchors, note, testPrefix = "fm-pool" }) {
  const [state, setState] = useState({ status: "loading" });
  const { profile: journey } = useJourney();
  const myCities = (journey?.where?.cities_confirmed || []).map((c) => c?.slug || c?.name || c).filter(Boolean);
  const cityKey = myCities.join("|");
  useEffect(() => {
    let live = true;
    fetchCourseMatches(subject, cityKey ? cityKey.split("|") : []).then((r) => live && setState(r.ok ? { status: "ok", data: r.data } : { status: "error" }));
    return () => { live = false; };
  }, [subject, cityKey]);
  const slug = subject.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`${testPrefix}-${slug}`}>
      <div className={kicker}>{role}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-serif text-xl text-stone-900">{subject}</h3>
        {state.status === "ok" && state.data.constraints?.myCities?.length > 0 && (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${state.data.constraints.myCityCount ? "bg-[#F5F0FF] text-[#7C3AED]" : "bg-stone-100 text-stone-600"}`} data-testid={`${testPrefix}-citycount-${slug}`}>
            {state.data.constraints.myCityCount} {subject} course{state.data.constraints.myCityCount === 1 ? "" : "s"} in your cities
          </span>
        )}
      </div>
      <p className="text-xs text-stone-500" data-testid={`${testPrefix}-note-${slug}`}>{note || rankNote(subject, top5)}</p>
      <p className="mt-2 inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] text-amber-900" data-testid={`${testPrefix}-exploratory-${slug}`}>
        Exploratory list · not a fit ranking
      </p>
      {state.status === "ok" && (
        <p className="mt-1 text-[12px] text-stone-600" data-testid={`${testPrefix}-constraints-${slug}`}>
          {state.data.constraints?.countries?.length
            ? `Only ${state.data.constraints.countries.join(", ")}, from your saved preferred countries.`
            : "All countries. Sign in and save preferred countries in your profile to narrow this."}
        </p>
      )}
      {state.status === "ok" && state.data.constraints?.myCities?.length > 0 && (
        <p className="mt-1 text-[12px] text-stone-600" data-testid={`${testPrefix}-mycities-${slug}`}>
          {state.data.constraints.myCityCount
            ? `Courses in your cities (${state.data.constraints.myCities.join(", ")}) come first, then the rest.`
            : `No ${subject} courses found in your cities (${state.data.constraints.myCities.join(", ")}), so these are from elsewhere.`}
        </p>
      )}
      {state.status === "loading" && <p className="mt-3 text-sm text-stone-500">Finding courses…</p>}
      {state.status === "error" && <p role="alert" className="mt-3 text-sm text-rose-700">Courses couldn't load just now.</p>}
      {state.status === "ok" && (state.data.items.length ? (
        <>
          <ul className="mt-3 divide-y divide-stone-100">
            {state.data.items.map((c) => (
              <li key={c.degreeId} className="py-3" data-testid={`fm-course-${c.degreeId}`}>
                <Link to={c.to} className="font-medium text-stone-900 underline-offset-2 hover:underline">{c.title}</Link>
                {c.inMyCities && <span className="ml-2 rounded-full bg-[#F5F0FF] px-2 py-0.5 text-[10px] font-medium text-[#7C3AED]" data-testid={`fm-course-mycity-${c.degreeId}`}>Your city</span>}
                {c.joint && <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-600">Joint / combined</span>}
                {c.details?.length > 0 && <div className="mt-0.5 text-[12px] text-stone-500" data-testid={`fm-course-details-${c.degreeId}`}>{c.details.join(" · ")}</div>}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-stone-600">
                  <Link to={c.university.to} className="hover:underline">{c.university.name}</Link>
                  {c.city && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" aria-hidden />
                      {c.city.to ? <Link to={c.city.to} className="hover:underline">{c.city.name}</Link> : c.city.name}
                    </span>
                  )}
                  {c.country && <span>· {c.country}</span>}
                </div>
                {anchors.map((a, i) => (
                  <div key={i} className="mt-0.5 text-[12px] text-stone-500" data-testid="fm-journey-unverified">
                    From {a.label}: journey time not verified
                  </div>
                ))}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-stone-500">{state.data.basis} {state.data.total > state.data.items.length ? `Showing ${state.data.items.length} of ${state.data.total}.` : ""}</p>
        </>
      ) : <p className="mt-3 text-sm text-stone-600">No courses in our data name {subject} in their title yet.</p>)}
    </div>
  );
}

export function InterestPools({ picked, top5, session, anchors, blockedReason, onSaved }) {
  const [mode, setMode] = useState("primary");
  const [primary, setPrimary] = useState(null);
  const [status, setStatus] = useState(null);
  const names = picked.map((p) => p.label);
  useEffect(() => {
    if (!session?.sessionId) return;
    fetchPlan(session.sessionId, session.accessToken).then((r) => {
      const p = r.ok && r.data.plan;
      if (p) { setMode(p.mode); setPrimary(p.interests[0]); }
    });
  }, [session?.sessionId, session?.accessToken]);
  const lead = names.includes(primary) ? primary : names[0];
  const ordered = mode === "primary" && lead ? [lead, ...names.filter((n) => n !== lead)] : names;
  const save = async () => {
    setStatus("saving");
    const r = await savePlan(session.sessionId, session.accessToken, { mode, interests: ordered });
    setStatus(r.ok ? "saved" : "error");
    if (r.ok) onSaved?.(r.data.plan);
  };
  if (!names.length) return null;
  return (
    <section id="fm-course-pools" className="mt-10 print:hidden" data-testid="fm-interest-pools">
      <div className={kicker}>Your subject interests</div>
      <h2 className="mt-1 font-serif text-2xl text-stone-900">Courses to explore for each interest</h2>
      <p className="mt-2 max-w-2xl text-sm text-stone-600">
        You can keep up to three interests: a main one and up to two serious alternatives, or explore them all equally.
        Each gets its own list, taken from this same Fast Match result. WUA doesn't merge them into a joint degree or re-weight your scores.
      </p>
      <p className="mt-1 text-sm text-stone-500" data-testid="fm-pools-hint">
        {names.length < 3 ? `Use “+ Choose” on the results above to add ${names.length ? "an alternative" : "an interest"} (up to 3).` : "You've chosen the maximum of three."}
      </p>
      <fieldset className="mt-4 flex flex-wrap gap-2" data-testid="fm-mode">
        <legend className="sr-only">How do you want to explore these?</legend>
        {[["primary", "I have a main interest"], ["equal", "I'm undecided, so explore them equally"]].map(([v, l]) => (
          <label key={v} className={`${btn} cursor-pointer ${mode === v ? "border-[#7C3AED] bg-[#F5F0FF] text-[#4C1D95]" : "border-stone-300 text-stone-700"}`}>
            <input type="radio" name="fm-mode" value={v} checked={mode === v} onChange={() => { setMode(v); setStatus(null); }} className="sr-only" data-testid={`fm-mode-${v}`} />
            {l}
          </label>
        ))}
      </fieldset>
      {mode === "primary" && names.length > 1 && (
        <label className="mt-3 block text-sm text-stone-700">
          Main interest
          <select value={lead} onChange={(e) => { setPrimary(e.target.value); setStatus(null); }} data-testid="fm-primary-select"
            className="ml-2 min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-sm">
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
      {session?.sessionId && (
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={save} disabled={!!blockedReason} className={`${btn} border-stone-900 bg-stone-900 text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-40`} data-testid="fm-plan-save">Save my interests</button>
          {blockedReason && <span className="text-sm text-amber-800" data-testid="fm-plan-blocked">{blockedReason}</span>}
          <span aria-live="polite" className="text-sm text-stone-600" data-testid="fm-plan-status">
            {status === "saving" ? "Saving…" : status === "saved" ? "Saved." : status === "error" ? "Couldn't save. Try again." : ""}
          </span>
        </div>
      )}
      <div className="mt-6 grid gap-4">
        {ordered.map((n, i) => (
          <CoursePool key={n} subject={n} top5={top5} anchors={anchors}
            note={picked.find((p) => p.label === n && p.source === "family")?.rank_label}
            role={mode === "equal" ? `Interest ${i + 1} of ${ordered.length}` : i === 0 ? "Main interest" : `Alternative ${i}`} />
        ))}
      </div>
    </section>
  );
}

// ---------------- next steps ----------------
export function NextSteps({ signedIn }) {
  const items = [
    { href: "#fm-course-pools", label: "Explore my course matches", hint: "Exploratory course lists for each interest, with their city (not a fit ranking)", testId: "fm-next-courses" },
    { to: "/cities/matcher", label: "Find places that fit me", hint: "Optional: cities that suit your budget and style", testId: "fm-next-places" },
    { to: signedIn ? "/realisations" : "/shortlist", label: "Help me weigh up both", hint: signedIn ? "My Realisations: what your course and place choices are telling you" : "Your shortlist: sign in to see My Realisations", testId: "fm-next-weigh" },
  ];
  return (
    <nav aria-label="Next steps" className="mt-8 grid gap-3 sm:grid-cols-3 print:hidden" data-testid="fm-next-steps">
      {items.map((it) => {
        const body = (
          <>
            <span className="font-medium text-stone-900">{it.label}</span>
            <span className="mt-1 block text-[13px] text-stone-600">{it.hint}</span>
            <ArrowRight className="mt-2 h-4 w-4 text-[#7C3AED]" aria-hidden />
          </>
        );
        const cls = "block rounded-2xl border border-stone-200 bg-white p-4 transition-colors hover:border-[#7C3AED] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]";
        return it.to
          ? <Link key={it.label} to={it.to} className={cls} data-testid={it.testId}>{body}</Link>
          : <a key={it.label} href={it.href} className={cls} data-testid={it.testId}>{body}</a>;
      })}
    </nav>
  );
}

